import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import useAxiosInstance from '../../lib/useAxiosInstance';
import { Dialog } from '@headlessui/react';
import { 
  X, 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Mail, 
  Phone, 
  Users, 
  FileText, 
  UserCheck 
} from 'lucide-react';
import { toast } from 'react-toastify';

const EventDetailsModal = ({ eventId, isOpen, onClose }) => {
  const axiosInstance = useAxiosInstance();
const queryClient = useQueryClient();
const user = JSON.parse(localStorage.getItem("user"));
  const fetchEventById = async (axiosInstance, eventId) => {
    const res = await axiosInstance.get(`api/eventScheduler/events/${eventId}`);
    return res.data?.data;
  };

  const updateEventStatus = async ({ id, status }) => {
    if (!user?._id || !user?.firstName || !user?.lastName) {
      throw new Error("Missing user information in localStorage");
    }
  
    let payload;
  
    if (status === "approved") {
      payload = {
        isApproved: true,
        approvedBy: {
          _id: user._id,
          name: `${user.firstName} ${user.lastName}`,
        },
      };
    } else if (status === "pending") {
      payload = {
        status: "pending",
      };
    } else {
      payload = {
        status: "cancelled",
      };
    }
  
    const res = await axiosInstance.put(`api/eventScheduler/events/${id}`, payload);
    if (!res?.data?.status) throw new Error(res?.data?.message || "Failed to update event");
    return res.data.data;          
  };
  
  const {
    mutate: updateEventStatusMutation,   
    isLoading: isUpdatingStatus,
  } = useMutation(updateEventStatus, {
    /** ------------------------------
     * 1. Optimistically update cache
     * -----------------------------*/
    onMutate: async ({ id, status }) => {
      await queryClient.cancelQueries(["Event-List"]); // pause outgoing refetch
  
      const previous = queryClient.getQueryData(["Event-List"]);
  
      // patch the cache so UI updates instantly
      queryClient.setQueryData(["Event-List"], (old) => {
        if (!old?.status) return old;
        return {
          ...old,
          data: {
            ...old.data,
            events: old.data.events.map((ev) =>
              ev._id === id
                ? {
                    ...ev,
                    isApproved: status === "approved" ? true : false,
                    approvedBy:
                      status === "approved"
                        ? { _id: user._id, name: `${user.firstName} ${user.lastName}` }
                        : status === "pending"
                          ? undefined
                          : ev.approvedBy,
                  }
                : ev
            ),
          },
        };
      });
      return { previous };
    },
  
    /** ------------------------------
     * 2. Roll back if the call fails
     * -----------------------------*/
    onError: (error, _vars, ctx) => {
      queryClient.setQueryData(["Event-List"], ctx.previous); // rollback
      toast.error(error?.message || "Failed to update event");
    },
  
    /** ------------------------------
     * 3. Always re‑fetch for freshness
     * -----------------------------*/
    onSettled: () => {
      queryClient.invalidateQueries(["Event-List"]); // server‑truth refetch
    },
  
    /** ------------------------------
     * 4. User feedback
     * -----------------------------*/
    onSuccess: () => {
      toast.success("Status updated successfully");
    },
  });

  const { data: event, isLoading, error } = useQuery(
    ['Event-Details', eventId],
    () => fetchEventById(axiosInstance, eventId),
    {
      enabled: !!eventId && isOpen,
    }
  );

  if (!isOpen) return null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleUpdateStatus = async (id, status) => {
    await updateEventStatusMutation({ id, status });
    onClose()
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50 flex items-center justify-center">
      <Dialog.Overlay className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative z-50 bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <Dialog.Title className="text-xl font-semibold heading-white">
            {event?.typeOfEvent==='visit' && "Visit"} {event?.typeOfEvent==='invitation' && "Invitation"} {event?.typeOfEvent==='appointment' && "Appointment"} Details
          </Dialog.Title>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 subheading" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <p className="text-red-500">Failed to load event details.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(event?.status)}`}>
                  {event?.status || 'Pending Approval'}
                </span>
                <span className="text-sm subheading">
                  Submitted on {formatDate(event?.createdAt || new Date())}
                </span>
              </div>

              {/* Event Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                    <h3 className="text-xl font-semibold heading-white mb-1">
                      {event?.eventTitle || 'Event Title'}
                    </h3>
                    <p className="subheading">{event?.typeOfEvent.charAt(0).toUpperCase() + event?.typeOfEvent.slice(1) || 'Meeting'}</p>
                </div>
                {/* Date and time */}
                <div>
                  <h4 className="text-md font-bold font-medium heading-white mb-2 flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    DATE & TIME
                  </h4>
                  <p className="subheading">
                    {formatDate(event?.start)}
                  </p>
                  <p className="subheading">
                    {formatTime(event?.start)} - {formatTime(event?.end)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Venue */}
                <div>
                  <h4 className="text-md font-bold heading-white mb-2 flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    {event?.typeOfEvent==='visit' && "ADDRESS"}
                    {event?.typeOfEvent==='invitation' && "VENUE"}
                    {event?.typeOfEvent==='appointment' && "Meeting Location"}
                  </h4>
                  <p className="subheading">{event?.address || 'Venue TBD'}</p>
                  {/* <p className="subheading">{event?.venue || 'Address TBD'}</p> */}
                </div>
                {/* Host/Contact */}
                <div>
                <h4 className="text-md font-bold heading-white mb-2 flex items-center">
                  <User className="w-4 h-4 mr-2" />
                  {/* HOST/CONTACT */}
                    {event?.typeOfEvent==='visit' && "VISITOR"}
                    {event?.typeOfEvent==='invitation' && "HOST"}
                    {event?.typeOfEvent==='appointment' && "ORGANIZER"}
                </h4>
                <div className="space-y-1">
                  <p className="subheading">{event?.firstName + " " + event.lastName || 'Event Host'}</p>
                  <p className="subheading">{event?.visitorType.toUpperCase() || 'Other'}</p>
                  <div className="flex items-center space-x-4 text-sm subheading">
                    <span className="flex items-center">
                      <Mail className="w-4 h-4 mr-1" />
                      {event?.email || 'host@example.com'}
                    </span>
                    <span className="flex items-center">
                      <Phone className="w-4 h-4 mr-1" />
                      {event?.phone || '(555) 123-4567'}
                    </span>
                  </div>
                </div>
              </div>
              </div>
              
              {/* Description */}
              <div>
                <h4 className="text-md font-bold heading-white mb-2 flex items-center">
                  <FileText className="w-4 h-4 mr-2" />
                  {event?.typeOfEvent==='visit' && "PURPOSE OF VISIT"}
                    {event?.typeOfEvent==='invitation' && "INVITATION DETAILS"}
                    {event?.typeOfEvent==='appointment' && "MEETING AGENDA"}
                </h4>
                <p className="subheading leading-relaxed">
                  {event?.description || 'No description provided.'}
                </p>
              </div>

              {/* Attendees */}
              {event?.attendees && event.attendees.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium heading-white mb-2 flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    {event?.typeOfEvent==='invitation' && "INVITEES"}
                    {event?.typeOfEvent==='appointment' && "ATTENDEES"}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {event.attendees.map((attendee, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                      >
                        <UserCheck className="w-4 h-4 mr-1" />
                        {attendee.name || attendee}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {event?.notes && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">
                    NOTES
                  </h4>
                  <p className="subheading leading-relaxed">
                    {event.notes}
                  </p>
                </div>
              )}

              {/* Submitted By */}
              <div>
                <h4 className="text-sm font-medium heading-white mb-2">
                  SUBMITTED BY
                </h4>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="subheading font-medium">
                      {event?.createdByUserName || ''}
                    </p>
                    <p className="subheading text-sm">
                      {event?.createdByUserRole.charAt(0).toUpperCase() + event?.createdByUserRole.slice(1) || 'Other'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-light-dark">
          <button
            onClick={onClose}
            className="px-4 py-2 heading-white bg-white border border-gray-300 rounded-lg hover:bg-gray-400 transition-colors"
          >
            Close
          </button>
          {event?.status === 'pending' && user?.role!=='data_entry_operator' && (
            <>
              <button
                onClick={() => handleUpdateStatus(eventId, 'cancelled')}
                className="px-4 py-2 text-red-700 bg-red-50 border border-red-300 rounded-lg hover:bg-red-100 transition-colors"
              >
                Decline
              </button>
              <button
                onClick={() => handleUpdateStatus(eventId, 'approved')}
                className="px-4 py-2 text-white bg-[#004877] rounded-lg hover:bg-blue-700 transition-colors"
              >
                Approve
              </button>
            </>
          )}
          {event?.status === 'approved' && user?.role!=='data_entry_operator' && (
            <button
                onClick={() => handleUpdateStatus(eventId, 'pending')}
                className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Disapprove
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default EventDetailsModal;